import { Box, Theme } from '@mui/material';
import { SxProps, useTheme } from '@mui/material/styles';
import { ForwardedRef, forwardRef, ReactNode, useMemo } from 'react';

import { Markdown } from '@/components/Markdown/Markdown';
import { TextEditorProvider } from '@/components/TextEditor/contexts/TextEditor.provider';
import {
  EducationalSlideBody,
  EducationalSlideH4,
} from '@/modules/presentation/clientPresentation/components/slides/EducationalSlide/components/EducationalSlide.components';

import {
  ContentContainer,
  IconContainer,
  ListItemContainer,
} from './PresentationListItem.styles';
import {
  ICON_TYPE_OPTIONS,
  IconColorToken,
  renderIconFromType,
} from './PresentationListItem.utils';

export interface PresentationListItemProps {
  /**
   * The icon component to use as the bullet point
   */
  icon?: ReactNode;
  /**
   * The main title text
   */
  title: string;
  /**
   * The description text that appears below the title
   */
  description: ReactNode;
  /**
   * The styles to apply to the list item
   */
  sx?: SxProps<Theme>;
  /**
   * The color of the heading text
   */
  color?: string;
  /**
   * The type of icon to display
   */
  iconType?: (typeof ICON_TYPE_OPTIONS)[number]['value'];
  /**
   * The number to display for the numbered icon
   */
  number?: number;
  /**
   * The color token for the icon (maps to data visualization colors)
   */
  iconColor?: IconColorToken;
}

function BasePresentationListItem(
  {
    icon,
    title,
    description,
    sx,
    color,
    iconType,
    number,
    iconColor,
  }: PresentationListItemProps,
  ref: ForwardedRef<HTMLDivElement>
) {
  const theme = useTheme();

  const editIcon = useMemo(() => {
    if (iconType && !icon) {
      return renderIconFromType({ iconType, number, theme, iconColor });
    }

    return icon;
  }, [icon, iconColor, iconType, number, theme]);

  return (
    <ListItemContainer ref={ref} sx={sx}>
      {editIcon && <IconContainer>{editIcon}</IconContainer>}
      <ContentContainer>
        <EducationalSlideH4 color={color}>{title}</EducationalSlideH4>
        <EducationalSlideBody>
          {typeof description === 'string' ? (
            <TextEditorProvider theme={'presentationContent'}>
              <Markdown markdown={description} EditorInner={Box} />
            </TextEditorProvider>
          ) : (
            description
          )}
        </EducationalSlideBody>
      </ContentContainer>
    </ListItemContainer>
  );
}

export const PresentationListItem = forwardRef<
  HTMLDivElement,
  PresentationListItemProps
>(BasePresentationListItem);
